<template>
    <nav class="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-800">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <div class="flex-shrink-0">
            <h1 class="text-xl font-bold text-white tracking-tight">
              <span class="text-emerald-400">&lt;</span>WW<span class="text-emerald-400">/&gt;</span>
            </h1>
          </div>
          <div class="hidden md:block">
            <div class="flex items-center space-x-1">
              <a v-for="item in navItems" 
                 :key="item.name" 
                 :href="item.href" 
                 :class="['nav-link', activeSection === item.id ? 'active-link' : '']"
                 @click="setActive(item.id)">
                {{ item.name }}
              </a>
            </div>
          </div>
          <button @click="toggleMobileMenu" class="md:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      <!-- Mobile Menu -->
      <transition name="slide-down">
        <div v-if="mobileMenuOpen" class="md:hidden bg-slate-900 border-t border-slate-800">
          <div class="px-4 py-3 space-y-1">
            <a v-for="item in navItems" 
               :key="item.name" 
               :href="item.href" 
               :class="['block px-4 py-3 rounded-lg transition-colors', activeSection === item.id ? 'text-emerald-400 bg-slate-800' : 'text-slate-300 hover:text-white hover:bg-slate-800']"
               @click="handleMobileClick(item.id)">
              {{ item.name }}
            </a>
          </div>
        </div>
      </transition>
    </nav>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const mobileMenuOpen = ref(false)
const activeSection = ref('home')

const navItems = [
  { name: 'Home', href: '#home', id: 'home' },
  { name: 'About', href: '#about', id: 'about' },
  { name: 'Skills', href: '#skills', id: 'skills' },
  { name: 'Projects', href: '#projects', id: 'projects' },
  { name: 'Contact', href: '#contact', id: 'contact' }
]

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
}

const setActive = (id) => {
  activeSection.value = id
}

const handleMobileClick = (id) => {
  setActive(id)
  closeMobileMenu()
}

let observer = null

onMounted(() => {
  const options = {
    root: null,
    rootMargin: '-50% 0px -50% 0px', // Activate when section is in middle of screen
    threshold: 0
  }

  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        activeSection.value = entry.target.id
      }
    })
  }, options)

  navItems.forEach(item => {
    const element = document.getElementById(item.id)
    if (element) observer.observe(element)
  })
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})
</script>

<style scoped>
.nav-link {
  @apply text-slate-400 hover:text-emerald-400 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200;
}

.active-link {
  @apply text-emerald-400 bg-slate-800/50;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
